/*
  Value-Added Solutions — Rack Mounted & Cabinetised Solutions page
  ─────────────────────────────────────
  Ported from value-added-solutions-solar-box.js. This page is a lighter
  variant with NO sizing calculator and NO CCTV pre-check stepper, so that
  logic (and the second, pre-check HubSpot form it drove) has been removed.
  What remains: the single bottom enquiry form + "Talk to an Engineer"
  modal (both posting to one HubSpot form), the industry conditional field,
  topic tabs, application/product tile switching, the FAQ accordion, the
  carousels, and the project detail modal.

  Scoped to run once per `[data-vas-solarbox-root]` element on the page
  (normally just one), so nothing here leaks into other sections.
*/
(function () {
  'use strict';

  function initRoot(root) {
    var hsPortalId = root.getAttribute('data-hs-portal-id') || '';
    var hsFormId = root.getAttribute('data-hs-form-id') || '';

    // On mobile, a newly-revealed detail panel sits below the fold and isn't
    // obviously "there" until the user scrolls — nudge them down to it.
    // Matches the theme's 750px CSS breakpoint, so desktop (where the panel
    // is already visible right below the tiles) is left alone.
    function scrollDetailIntoViewOnMobile(el) {
      if (window.innerWidth >= 750) return;
      // Double rAF: waits a full extra paint cycle so the browser has
      // definitely committed the display:none -> block layout change
      // before we calculate where to scroll to. A single rAF is not
      // always enough on mobile browsers and can scroll to a stale
      // (pre-layout) position.
      window.requestAnimationFrame(function () {
        window.requestAnimationFrame(function () {
          el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        });
      });
    }

    /* ── HubSpot submission (shared by all forms in this section) ── */
    function submitToHubSpot(form, msgEl, btn, fieldNameWhitelist, portalIdOverride, formIdOverride) {
      var targetPortalId = portalIdOverride || hsPortalId;
      var targetFormId = formIdOverride || hsFormId;
      // Honeypot check — if the hidden field has been filled in, silently
      // treat as spam: no HubSpot request, but show a normal success state
      // so bots don't learn the field is being checked.
      var hp = form.querySelector('.vas-hp-input');
      if (hp && hp.value.trim() !== '') {
        msgEl.style.display = 'block';
        msgEl.innerHTML = '<p style="color:#2ea84a;font-weight:600;font-size:1.3rem;padding:1rem;background:#f2fff5;border-left:3px solid #2ea84a;border-radius:0 8px 8px 0">&#10003; Enquiry submitted successfully. A Powerbox specialist will be in touch shortly.</p>';
        form.reset();
        return;
      }

      var missing = [];
      Array.prototype.forEach.call(form.elements, function (el) {
        if (!el.name || el.type === 'submit' || !el.required) return;
        if (!el.value || el.value.trim() === '') {
          missing.push(el);
          el.style.borderColor = 'var(--pbx-color-brand-red, #DB2727)';
        } else {
          el.style.borderColor = '';
        }
      });
      if (missing.length > 0) {
        msgEl.style.display = 'block';
        msgEl.innerHTML = '<p style="color:var(--pbx-color-brand-red, #DB2727);font-size:1.3rem;padding:1rem;background:#fdf2f2;border-left:3px solid var(--pbx-color-brand-red, #DB2727);border-radius:0 8px 8px 0">Please complete all required fields before submitting.</p>';
        missing[0].focus();
        return;
      }
      Array.prototype.forEach.call(form.elements, function (el) { el.style.borderColor = ''; });
      msgEl.style.display = 'none';

      if (!targetPortalId || !targetFormId) {
        msgEl.style.display = 'block';
        msgEl.innerHTML = '<p style="color:var(--pbx-color-brand-red, #DB2727);font-size:1.3rem">This form is not yet connected to HubSpot — set the Portal ID and Form ID in the section settings.</p>';
        return;
      }

      var fields = [];
      Array.prototype.forEach.call(form.elements, function (el) {
        if (!el.name || el.type === 'submit') return;
        if (el.classList && el.classList.contains('vas-hp-input')) return; // never send the honeypot value
        if (fieldNameWhitelist && fieldNameWhitelist.indexOf(el.name) === -1) return; // only send known HubSpot properties, when a whitelist is given
        if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
        fields.push({ name: el.name, value: el.value });
      });
      var payload = { fields: fields, context: { pageUri: window.location.href, pageName: document.title } };

      btn.disabled = true;
      var originalText = btn.textContent;
      btn.textContent = 'Submitting…';
      msgEl.style.display = 'none';

      fetch('https://api.hsforms.com/submissions/v3/integration/submit/' + targetPortalId + '/' + targetFormId, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      })
        .then(function (res) {
          if (res.ok) {
            msgEl.style.display = 'block';
            msgEl.innerHTML = '<p style="color:#2ea84a;font-weight:600;font-size:1.3rem;padding:1rem;background:#f2fff5;border-left:3px solid #2ea84a;border-radius:0 8px 8px 0">&#10003; Enquiry submitted successfully. A Powerbox specialist will be in touch shortly.</p>';
            form.reset();
          } else {
            return res.json().then(function (e) { throw e; });
          }
        })
        .catch(function () {
          msgEl.style.display = 'block';
          msgEl.innerHTML = '<p style="color:var(--pbx-color-brand-red, #DB2727);font-size:1.3rem;padding:1rem;background:#fdf2f2;border-left:3px solid var(--pbx-color-brand-red, #DB2727);border-radius:0 8px 8px 0">Something went wrong. Please try again or contact us directly.</p>';
        })
        .finally(function () { btn.disabled = false; btn.textContent = originalText; });
    }

    /* ── Industry conditional field (bottom form + engineer modal) ── */
    root.querySelectorAll('.js-vas-industry-select').forEach(function (select) {
      var wrap = select.closest('form').querySelector('.js-vas-industry-other-wrap');
      var input = wrap ? wrap.querySelector('input') : null;
      function sync() {
        var isOther = select.value === 'Other';
        if (wrap) wrap.classList.toggle('is-shown', isOther);
        if (input) input.required = isOther;
      }
      select.addEventListener('change', sync);
      sync();
    });

    /* ── Bottom contact form — progressive disclosure + submit ── */
    var bottomForm = root.querySelector('.js-vas-bottom-form');
    if (bottomForm) {
      var triggerFields = bottomForm.querySelectorAll('.js-vas-pf-trigger');
      var reveal = function () { bottomForm.classList.add('is-expanded'); };
      triggerFields.forEach(function (f) { f.addEventListener('focus', reveal, { once: true }); });
      bottomForm.addEventListener('submit', function (e) {
        e.preventDefault();
        submitToHubSpot(bottomForm, bottomForm.querySelector('.js-vas-form-msg'), bottomForm.querySelector('button[type="submit"]'));
      });
    }

    /* ── Talk to an Engineer modal ── */
    var engineerOverlay = root.querySelector('.js-vas-engineer-modal-overlay');
    var engineerForm = root.querySelector('.js-vas-engineer-form');
    function openEngineerModal() { if (engineerOverlay) { engineerOverlay.classList.add('is-open'); document.body.style.overflow = 'hidden'; } }
    function closeEngineerModal() { if (engineerOverlay) { engineerOverlay.classList.remove('is-open'); document.body.style.overflow = ''; } }
    root.querySelectorAll('.js-vas-talk-engineer').forEach(function (btn) {
      btn.addEventListener('click', function (e) { e.preventDefault(); openEngineerModal(); });
    });
    if (engineerOverlay) {
      var engineerClose = engineerOverlay.querySelector('.js-vas-engineer-modal-close');
      if (engineerClose) engineerClose.addEventListener('click', closeEngineerModal);
      engineerOverlay.addEventListener('click', function (e) { if (e.target === engineerOverlay) closeEngineerModal(); });
    }
    if (engineerForm) {
      engineerForm.addEventListener('submit', function (e) {
        e.preventDefault();
        submitToHubSpot(engineerForm, engineerForm.querySelector('.js-vas-form-msg'), engineerForm.querySelector('button[type="submit"]'));
      });
    }

    /* ── Topic selector tabs (One Supplier) ── */
    root.querySelectorAll('.js-vas-topic-btn').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var topic = btn.getAttribute('data-topic');
        root.querySelectorAll('.js-vas-topic-btn').forEach(function (b) { b.classList.remove('is-active'); });
        root.querySelectorAll('.js-vas-topic-panel').forEach(function (p) { p.classList.remove('is-active'); });
        btn.classList.add('is-active');
        var panel = root.querySelector('.js-vas-topic-panel[data-topic="' + topic + '"]');
        if (panel) panel.classList.add('is-active');
      });
    });

    /* ── Topic tab scroll arrows ── */
    // On narrow screens the tab row overflows and the extra tabs are hidden
    // off-screen with no obvious cue. These arrows only appear when there is
    // more to scroll to in that direction, and hide themselves otherwise.
    (function () {
      var scroller = root.querySelector('.js-vas-topic-scroller');
      if (!scroller) return;
      var track = scroller.querySelector('.js-vas-topic-selector');
      var prev = scroller.querySelector('.js-vas-topic-arrow-prev');
      var next = scroller.querySelector('.js-vas-topic-arrow-next');
      if (!track || !prev || !next) return;

      function update() {
        var max = track.scrollWidth - track.clientWidth;
        prev.classList.toggle('is-visible', max > 4 && track.scrollLeft > 4);
        next.classList.toggle('is-visible', max > 4 && track.scrollLeft < max - 4);
      }
      function scrollTabs(dir) {
        track.scrollBy({ left: dir * track.clientWidth * 0.7, behavior: 'smooth' });
      }

      prev.addEventListener('click', function () { scrollTabs(-1); });
      next.addEventListener('click', function () { scrollTabs(1); });
      track.addEventListener('scroll', update, { passive: true });
      window.addEventListener('resize', update);
      window.addEventListener('load', update);
      update();
    })();

    /* ── Application tiles (Common Applications) ── */
    root.querySelectorAll('.js-vas-app-tile').forEach(function (tile) {
      tile.addEventListener('click', function (e) {
        e.preventDefault();
        var app = tile.getAttribute('data-app');
        root.querySelectorAll('.js-vas-app-tile').forEach(function (t) { t.classList.remove('is-active'); });
        root.querySelectorAll('.js-vas-app-detail').forEach(function (d) { d.classList.remove('is-active'); });
        tile.classList.add('is-active');
        var detail = root.querySelector('.js-vas-app-detail[data-app="' + app + '"]');
        if (detail) {
          detail.classList.add('is-active');
          scrollDetailIntoViewOnMobile(detail);
        }
      });
    });

    /* ── Product tiles (What Goes Into a Solar Box Solution) ── */
    root.querySelectorAll('.js-vas-product-tile').forEach(function (tile) {
      tile.addEventListener('click', function (e) {
        e.preventDefault();
        var product = tile.getAttribute('data-product');
        root.querySelectorAll('.js-vas-product-tile').forEach(function (t) { t.classList.remove('is-active'); });
        root.querySelectorAll('.js-vas-product-detail').forEach(function (d) { d.classList.remove('is-active'); });
        tile.classList.add('is-active');
        var detail = root.querySelector('.js-vas-product-detail[data-product="' + product + '"]');
        if (detail) {
          detail.classList.add('is-active');
          scrollDetailIntoViewOnMobile(detail);
        }
      });
    });

    /* ── FAQ accordion ── */
    root.querySelectorAll('.vas-faq-q').forEach(function (q) {
      q.addEventListener('click', function () { q.closest('.vas-faq-item').classList.toggle('is-open'); });
    });

    /* ── Carousels — generic prev/next by data-carousel key ── */
    root.querySelectorAll('.js-vas-carousel-prev, .js-vas-carousel-next').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var key = btn.getAttribute('data-carousel');
        var track = root.querySelector('.js-vas-carousel[data-carousel="' + key + '"]');
        if (!track) return;
        var amount = track.firstElementChild ? track.firstElementChild.offsetWidth + 16 : 300;
        track.scrollBy({ left: btn.classList.contains('js-vas-carousel-prev') ? -amount : amount, behavior: 'smooth' });
      });
    });

    /* ── Project detail modal ── */
    var projectOverlay = root.querySelector('.js-vas-project-modal-overlay');
    if (projectOverlay) {
      function closeProjectModal() { projectOverlay.classList.remove('is-open'); document.body.style.overflow = ''; }

      var pmImages = [];
      var pmCurrentImg = 0;

      function pmShowImage(idx) {
        var mediaEl = projectOverlay.querySelector('.js-vas-pm-media');
        var counter = projectOverlay.querySelector('.js-vas-pm-img-counter');
        var nav = projectOverlay.querySelector('.js-vas-pm-img-nav');
        if (!pmImages.length) return;
        pmCurrentImg = (idx + pmImages.length) % pmImages.length;
        var url = pmImages[pmCurrentImg];
        var isVideo = /\.(mp4|webm|ogg|mov)(\?|$)/i.test(url);
        if (isVideo) {
          var existingImg = mediaEl.querySelector('.pm-img-display');
          if (existingImg) existingImg.remove();
          var vid = mediaEl.querySelector('video') || document.createElement('video');
          vid.className = 'pm-img-display'; vid.src = url; vid.controls = true; vid.setAttribute('playsinline', '');
          vid.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;display:block;';
          mediaEl.appendChild(vid);
        } else {
          var existingVid = mediaEl.querySelector('video');
          if (existingVid) existingVid.remove();
          var img = mediaEl.querySelector('.pm-img-display') || document.createElement('img');
          img.className = 'pm-img-display'; img.src = url; img.alt = '';
          img.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;object-fit:contain;display:block;';
          mediaEl.appendChild(img);
        }
        if (pmImages.length > 1) {
          counter.textContent = (pmCurrentImg + 1) + ' / ' + pmImages.length;
          counter.style.display = '';
          nav.style.display = 'flex';
        } else {
          counter.style.display = 'none';
          nav.style.display = 'none';
        }
      }

      var pmPrev = projectOverlay.querySelector('.js-vas-pm-img-prev');
      var pmNext = projectOverlay.querySelector('.js-vas-pm-img-next');
      if (pmPrev) pmPrev.addEventListener('click', function (e) { e.stopPropagation(); pmShowImage(pmCurrentImg - 1); });
      if (pmNext) pmNext.addEventListener('click', function (e) { e.stopPropagation(); pmShowImage(pmCurrentImg + 1); });

      root.querySelectorAll('.js-vas-project-card').forEach(function (card) {
        card.addEventListener('click', function () {
          try { pmImages = JSON.parse(card.dataset.mediaUrls || '[]'); } catch (e) { pmImages = []; }
          pmCurrentImg = 0;
          var mediaEl = projectOverlay.querySelector('.js-vas-pm-media');
          mediaEl.querySelectorAll('.pm-img-display, video').forEach(function (el) { el.remove(); });
          pmShowImage(0);
          projectOverlay.querySelector('.js-vas-pm-sub').textContent = card.dataset.sub || '';
          projectOverlay.querySelector('.js-vas-pm-title').textContent = card.dataset.title || '';
          projectOverlay.querySelector('.js-vas-pm-desc').innerHTML = (card.dataset.desc || '').replace(/\n\n+/g, '<br><br>').replace(/\n/g, '<br>');
          var ctaLink = projectOverlay.querySelector('.js-vas-pm-cta');
          var ctaBtn = projectOverlay.querySelector('.js-vas-pm-cta-btn');
          if (card.dataset.hasCta === 'true') {
            ctaLink.href = card.dataset.ctaUrl;
            ctaBtn.textContent = card.dataset.ctaLabel || 'View Complete Case Study';
            ctaLink.style.display = '';
          } else {
            ctaLink.style.display = 'none';
          }
          projectOverlay.classList.add('is-open');
          document.body.style.overflow = 'hidden';
        });
      });
      var projectClose = projectOverlay.querySelector('.js-vas-project-modal-close');
      if (projectClose) projectClose.addEventListener('click', closeProjectModal);
      projectOverlay.addEventListener('click', function (e) { if (e.target === projectOverlay) closeProjectModal(); });
    }
  }

  function init() {
    document.querySelectorAll('[data-vas-solarbox-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
